import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    useDisclosure,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Text,
    Alert,
    Box,
    Tag,
    TagCloseButton
} from '@chakra-ui/react';

import { ProfileClass } from '../../../core/profile';
import LandService, { LandClass } from '../../../core/land';
import { DataContext, ProfileContext } from '../../../core/contexts';

import { Selector } from '../shared/asyncSelector';
import { UserPrivacy, UserForm, UserBox } from '../user';

export default function LandShare(props: {
    data: LandClass;
    as: 'button' | 'link';
    beforeClose?: Function;
}): React.ReactElement {
    const landService = new LandService();

    const { t } = useTranslation();
    const { user, getSharedProfiles } = useContext(ProfileContext);
    const { lands, updateLand } = useContext(DataContext);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [profiles, setProfiles] = useState<ProfileClass[]>([]);
    const [newEditors, setNewEditors] = useState<ProfileClass[]>([]);
    const [newReaders, setNewReaders] = useState<ProfileClass[]>([]);
    const [land, setLand] = useState<LandClass>();

    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!land) {
            return;
        }
        setSaving(true);
        land.editors = [...land.editorsUser, ...newEditors].reduce(
            (acc: string[], user: ProfileClass): string[] => {
                return acc.includes(user.id) ? acc : [...acc, user.id];
            },
            []
        );
        land.readers = [...land.readersUser, ...newReaders].reduce(
            (acc: string[], user: ProfileClass): string[] => {
                return acc.includes(user.id) ? acc : [...acc, user.id];
            },
            []
        );
        await landService.save(new LandClass(land));
        await updateLand(land.id);
        props.beforeClose && props.beforeClose(e);
        setSaving(false);
        onClose();
    };

    const queryPublicProfiles = () => {
        getSharedProfiles(true).then((publicProfiles) => {
            setProfiles(publicProfiles as ProfileClass[]);
            setLoading(false);
        });
    };

    const filterOptions = (profile: ProfileClass) => {
        return (
            land &&
            ![
                user.id,
                land.owner,
                ...[
                    ...land.readersUser,
                    ...land.editorsUser,
                    ...newReaders,
                    ...newEditors
                ].map((p) => p.id)
            ].includes(profile.id)
        );
    };

    useEffect(() => {
        if (isOpen) {
            setNewEditors([]);
            setNewReaders([]);
            setLand(props.data);
            queryPublicProfiles();
        }
    }, [isOpen]);

    return (
        <>
            {props.as === 'button' && (
                <Button
                    size='sm'
                    colorScheme='green'
                    variant='outline'
                    onClick={onOpen}
                >
                    {t('shareModal.title')}
                </Button>
            )}

            {props.as === 'link' && (
                <Box onClick={onOpen}>
                    <Text
                        fontSize='md'
                        style={{ fontWeight: 'bold' }}
                        color='gray.500'
                    >
                        {t('shareModal.title')}
                    </Text>
                </Box>
            )}

            <Modal isOpen={isOpen} onClose={onClose} size='xl' isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{t('shareModal.title')}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {!loading && land ? (
                            user.public ? (
                                <form
                                    id='shareForm'
                                    onSubmit={onSubmit}
                                    noValidate
                                >
                                    {/* READERS */}
                                    <Alert
                                        status='info'
                                        mb={2}
                                        py='2'
                                        variant='left-accent'
                                        size={'xs'}
                                    >
                                        {/* <AlertIcon /> */}
                                        <Text fontSize='sm'>
                                            {t('shareModal.reader')}
                                        </Text>
                                    </Alert>
                                    <Text>{t('shareModal.addReaders')}</Text>
                                    <Selector
                                        name='readers'
                                        isMulti={true}
                                        onChange={(e: any) => {
                                            setNewReaders(
                                                e.map((user: any) => user.value)
                                            );
                                        }}
                                        options={profiles
                                            ?.filter(filterOptions)
                                            .map((user: ProfileClass) => {
                                                return {
                                                    label:
                                                        user.name +
                                                        ' - ' +
                                                        user.email,
                                                    value: user
                                                };
                                            })}
                                    ></Selector>
                                    <p>{t('shareModal.currentReaders')}</p>
                                    {land.readersUser.map(
                                        (reader: any, index: number) => (
                                            <Tag
                                                key={reader.id}
                                                mt='2'
                                                mr='2'
                                                p='2'
                                                variant='outline'
                                                colorScheme='gray'
                                            >
                                                <UserBox
                                                    guest={reader}
                                                    land={land}
                                                />
                                                <TagCloseButton
                                                    onClick={() => {
                                                        setLand({
                                                            ...land,
                                                            readersUser:
                                                                land.readersUser.filter(
                                                                    (r) =>
                                                                        r.id !==
                                                                        reader.id
                                                                )
                                                        } as LandClass);
                                                    }}
                                                />
                                            </Tag>
                                        )
                                    )}

                                    {/* EDITORS */}
                                    <Box my='10' />
                                    <Alert
                                        status='warning'
                                        mb={2}
                                        py='2'
                                        variant='left-accent'
                                        size={'xs'}
                                    >
                                        {/* <AlertIcon /> */}
                                        <Text fontSize='sm'>
                                            {t('shareModal.editor')}
                                        </Text>
                                    </Alert>
                                    <Text>{t('shareModal.addEditors')}</Text>
                                    <Selector
                                        name='editors'
                                        isMulti={true}
                                        onChange={(e: any) => {
                                            setNewEditors(
                                                e.map((user: any) => user.value)
                                            );
                                        }}
                                        options={profiles
                                            ?.filter(filterOptions)
                                            .map((user: ProfileClass) => {
                                                return {
                                                    label:
                                                        user.name +
                                                        ' - ' +
                                                        user.email,
                                                    value: user
                                                };
                                            })}
                                    ></Selector>
                                    <p>{t('shareModal.currentEditors')}</p>
                                    {land.editorsUser.map(
                                        (editor: any, index: number) => (
                                            <Tag
                                                key={editor.id}
                                                mt='2'
                                                mr='2'
                                                p='2'
                                                variant='outline'
                                                colorScheme='gray'
                                            >
                                                <UserBox
                                                    guest={editor}
                                                    land={land}
                                                />
                                                <TagCloseButton
                                                    onClick={() => {
                                                        setLand({
                                                            ...land,
                                                            editorsUser:
                                                                land.editorsUser.filter(
                                                                    (e) =>
                                                                        e.id !==
                                                                        editor.id
                                                                )
                                                        } as LandClass);
                                                    }}
                                                />
                                            </Tag>
                                        )
                                    )}
                                </form>
                            ) : (
                                <Box mb='10'>
                                    <UserForm />
                                    <Box mt='4'>
                                        <UserPrivacy />
                                    </Box>
                                </Box>
                            )
                        ) : (
                            <></>
                        )}
                    </ModalBody>

                    {user.public && (
                        <ModalFooter>
                            <Button
                                colorScheme='orange'
                                variant='outline'
                                size='sm'
                                mr={3}
                                onClick={onClose}
                            >
                                {t('form.close')}
                            </Button>
                            <Button
                                colorScheme='green'
                                variant='outline'
                                size='sm'
                                type='submit'
                                form='shareForm'
                                isLoading={saving}
                            >
                                {t('form.save')}
                            </Button>
                        </ModalFooter>
                    )}
                </ModalContent>
            </Modal>
        </>
    );
}
